import { useEffect } from "react";
import Navbar from "../components/Navbar";
import ActivityTicker from "../components/ActivityTicker";
import Footer from "../components/Footer";
import HeroSection from "../components/sections/HeroSection";
import TrustBar from "../components/sections/TrustBar";
import PlatformsSection from "../components/sections/PlatformsSection";
import BenefitsBento from "../components/sections/BenefitsBento";
import VolumeTiersSection from "../components/sections/VolumeTiersSection";
import DepositBonusSection from "../components/sections/DepositBonusSection";
import HowItWorksSection from "../components/sections/HowItWorksSection";
import PricingSection from "../components/sections/PricingSection";
import ComparisonTable from "../components/sections/ComparisonTable";
import StatsSection from "../components/sections/StatsSection";
import ResultsShowcase from "../components/sections/ResultsShowcase";
import FAQSection from "../components/sections/FAQSection";
import FinalCTA from "../components/sections/FinalCTA";
import ComplianceInfoSection from "../components/sections/ComplianceInfoSection";
import SeoClusterSection from "../components/sections/SeoClusterSection";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { api } from "../lib/api";

const SITE_URL = "https://adrise-agency.com";

const homeJsonLd = [
    {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Adrise",
        url: SITE_URL,
        logo: `${SITE_URL}/logo.png`,
        description:
            "Managed ad operations infrastructure for Meta, Google, TikTok, Snapchat, Bing, Taboola and Outbrain with flexible billing and volume-based fee tiers.",
        sameAs: [
            "https://t.me/adrise",
            "https://x.com/adrise",
        ],
        contactPoint: [
            {
                "@type": "ContactPoint",
                contactType: "customer support",
                availableLanguage: ["English"],
                url: `${SITE_URL}/signup`,
            },
        ],
    },
    {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Adrise",
        url: SITE_URL,
        potentialAction: {
            "@type": "SearchAction",
            target: `${SITE_URL}/blog?q={search_term_string}`,
            "query-input": "required name=search_term_string",
        },
    },
    {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Agency Ad Accounts (Credit Line)",
        provider: { "@type": "Organization", name: "Adrise", url: SITE_URL },
        areaServed: "Worldwide",
        offers: [
            {
                "@type": "Offer",
                name: "Agency Ad Account Access (No Subscription)",
                price: "0",
                priceCurrency: "USD",
                description:
                    "No monthly subscription. Managed agency ad accounts on all major platforms with a volumetric top-up fee from 2.5% (white-hat), sliding down to 1% at scale.",
            },
        ],
        aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "320",
            bestRating: "5",
            worstRating: "1",
        },
    },
    {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
            {
                "@type": "Question",
                name: "What is a Platinum HIVA Partner account?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Platinum HIVA (High Value Advertiser) is the top-tier internal Business Manager trust classification within Meta's ecosystem, with equivalent agency seats on Google, TikTok, Snapchat, Bing, Taboola and Outbrain. It correlates with greater account stability, faster moderation, lower CPMs and direct human-rep escalation.",
                },
            },
            {
                "@type": "Question",
                name: "How does the Credit Line work?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Adrise manages account-side billing operations and wallet funding flow. Clients top up in supported stablecoins and campaign budgets are allocated according to policy-compliant operational controls.",
                },
            },
            {
                "@type": "Question",
                name: "How does the volumetric fee structure work?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "Fees are volume-based and may decrease as monthly spend increases. Specific pricing and vertical terms are defined during onboarding and may vary by platform policy constraints.",
                },
            },
            {
                "@type": "Question",
                name: "What is cashback and who qualifies?",
                acceptedAnswer: {
                    "@type": "Answer",
                    text: "High-volume clients may become eligible for cashback tiers depending on spend and account history. Final terms are documented in service agreement and platform policy constraints.",
                },
            },
        ],
    },
];

export default function LandingPage() {
    useEffect(() => {
        api.get("/health").catch(() => {});
    }, []);

    return (
        <main
            data-testid="landing-page"
            className="bg-background text-foreground"
        >
            <SEOHead
                title="Adrise — Agency Ad Accounts for Meta, Google, TikTok & More | No Subscription, Crypto-Funded"
                description="Whitelisted agency ad accounts on Meta, Google Ads, TikTok, Snapchat, Bing, Taboola and Outbrain. No monthly subscription — volumetric top-up fee from 2.5% down to 1%. Fund with USDT/USDC, accounts live in under 5 minutes."
                canonical={SITE_URL}
                keywords={[
                    "agency ad accounts",
                    "media buying operations",
                    "meta agency account",
                    "google agency account",
                    "tiktok agency account",
                    "credit line ad accounts",
                    "ad operations support",
                    "volume-based ad fees",
                    "stablecoin ad funding",
                    "whitelisted ad infrastructure",
                ]}
                jsonLd={homeJsonLd}
            />

            <Navbar />
            <HeroSection />
            <ActivityTicker />
            <TrustBar />
            <PlatformsSection />
            <BenefitsBento />
            <VolumeTiersSection />
            <DepositBonusSection />
            <HowItWorksSection />
            <PricingSection />
            <ComparisonTable />
            <StatsSection />
            <ResultsShowcase />
            <FAQSection />
            <SeoClusterSection />
            <ComplianceInfoSection />
            <FinalCTA />
            <Footer />
            <FloatingContact />
        </main>
    );
}
